// The document title, written in place, and the caret's way between the title
// and the body.
//
// Promise:
// - The title is one line that wraps (a textarea whose height comes from a
//   hidden copy of its text, so a late font never cuts it off). Line breaks are
//   never inserted; pasted ones become spaces. No frame, no ring (plain-field).
// - Enter (or → at its end) moves into the body on a new first line, ↓ moves to
//   the body's start; Backspace / ← at the body's start and ↑ on its first line
//   come back to the title's end. IME composition is never taken as a key.
import { type Editor, Extension } from "@tiptap/core";
import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useRef,
} from "react";
import { isImeComposing } from "../ui/ime";
import { startTyping } from "./editor/tap-to-type";

/** The look of the title: the field and its hidden copy share it. */
export const TITLE_TEXT =
  "px-0 py-2 text-[24px] leading-[1.35] font-semibold text-fg";

export type TitleExit = "enter" | "move";

/** Reads a keydown in the title as a move into the body, or null. */
export function titleExitKey(
  e: KeyboardEvent<HTMLTextAreaElement>,
): TitleExit | null {
  const n = e.nativeEvent;
  if (isImeComposing(n)) return null;
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return null;
  if (e.key === "Enter") return "enter";
  if (e.key === "ArrowDown") return "move";
  if (e.key === "ArrowRight") {
    const f = e.currentTarget;
    const end = f.value.length;
    return f.selectionStart === end && f.selectionEnd === end ? "enter" : null;
  }
  return null;
}

/**
 * Moves the caret into the body. Call it inside the keystroke: the DOM focus is
 * set first, so the next character (an IME's first one too) lands in the body.
 * On a phone the body opens reading (not writable): it is made writable first,
 * in the same keystroke, so the focus moves and the keyboard stays up (a focus
 * moved later, outside the keystroke, does not bring the keyboard on iOS).
 */
export function leaveTitle(editor: Editor | null, how: TitleExit): boolean {
  if (!editor) return false;
  startTyping(editor);
  editor.view.dom.focus();
  if (how === "move") {
    editor.commands.focus("start");
    return true;
  }
  const first = editor.state.doc.firstChild;
  if (first?.type.name === "paragraph" && first.content.size === 0)
    editor.commands.focus("start");
  else editor.chain().insertContentAt(0, { type: "paragraph" }).focus(1).run();
  return true;
}

export function focusTitleEnd(
  field: HTMLTextAreaElement | HTMLInputElement | null | undefined,
): boolean {
  if (!field) return false;
  field.focus();
  const end = field.value.length;
  field.setSelectionRange(end, end);
  return true;
}

interface PosLike {
  parentOffset: number;
  depth: number;
  index(depth: number): number;
}

function inFirstBlock(selection: { empty: boolean; $from: PosLike }): boolean {
  if (!selection.empty) return false;
  const $from = selection.$from;
  for (let depth = $from.depth; depth > 0; depth--)
    if ($from.index(depth - 1) !== 0) return false;
  return true;
}

/**
 * Backspace / ← at the very start of the body, ↑ on its first line: back to the
 * title. Below the default priority, so lists and input rules use the key first.
 */
export const LeaveBodyToTitle = Extension.create<{ onLeave(): boolean }>({
  name: "trameriaLeaveBodyToTitle",
  priority: 50,
  addOptions() {
    return { onLeave: () => false };
  },
  addKeyboardShortcuts() {
    const atStart = () => {
      const s = this.editor.state.selection;
      return inFirstBlock(s) && s.$from.parentOffset === 0;
    };
    return {
      Backspace: () => atStart() && this.options.onLeave(),
      ArrowLeft: () => atStart() && this.options.onLeave(),
      ArrowUp: () => {
        const { selection } = this.editor.state;
        if (!inFirstBlock(selection) || !selection.$from.parent.isTextblock)
          return false;
        if (!this.editor.view.endOfTextblock("up")) return false;
        return this.options.onLeave();
      },
    };
  },
});

export function TitleField({
  fieldRef,
  value,
  label,
  placeholder,
  autoFocus = false,
  onValueChange,
  onBlur,
  onKeyDown,
}: {
  fieldRef?: RefObject<HTMLTextAreaElement | null>;
  value: string;
  label: string;
  placeholder?: string;
  autoFocus?: boolean;
  onValueChange(value: string): void;
  onBlur?(): void;
  onKeyDown?(e: KeyboardEvent<HTMLTextAreaElement>): void;
}) {
  const own = useRef<HTMLTextAreaElement>(null);
  return (
    // The hidden copy and the field share one grid cell: the copy sets the
    // height, the field stretches to it.
    <div className="grid w-full grid-cols-[minmax(0,1fr)]">
      <textarea
        ref={fieldRef ?? own}
        rows={1}
        aria-label={label}
        value={value}
        placeholder={placeholder}
        enterKeyHint="done"
        maxLength={200}
        spellCheck={false}
        // biome-ignore lint/a11y/noAutofocus: opening a new document means typing its title
        autoFocus={autoFocus}
        onChange={(e) => onValueChange(e.target.value.replace(/[\r\n]+/g, " "))}
        onBlur={onBlur}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.key === "Enter" && !isImeComposing(e.nativeEvent))
            e.preventDefault();
        }}
        className={`${TITLE_TEXT} plain-field col-start-1 row-start-1 w-full resize-none overflow-hidden border-none bg-transparent outline-none`}
      />
      <span
        aria-hidden="true"
        className={`${TITLE_TEXT} invisible col-start-1 row-start-1 whitespace-pre-wrap break-words`}
      >
        {`${value} `}
      </span>
    </div>
  );
}

/** The title when it cannot be changed (archived, newer document). */
export function TitleText({ children }: { children: ReactNode }) {
  return (
    <h2 className={`${TITLE_TEXT} select-text whitespace-pre-wrap break-words`}>
      {children}
    </h2>
  );
}
